import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import queryString from "query-string";
import AnalystRating from "../../components/user-profile-lite/AnalystRating";
import AccountDetailsPanel from "./AccountDetailsPanel";
import AvatarModal from "./AvatarModal";
import BillingPanel from "./BillingPanel";
import ChangePasswordPanel from "./ChangePasswordPanel";
import FavoritesPanel from "./FavoritesPanel";
import NotificationsPanel from "./NotificationsPanel";
import {
  ProfileAvatarEdit,
  ProfileAvatarFallback,
  ProfileAvatarImg,
  ProfileAvatarWrap,
  ProfileEmail,
  ProfileHero,
  ProfileHeroRow,
  ProfileHeroText,
  ProfileHomeLink,
  ProfileHomeWrap,
  ProfileInner,
  ProfileMain,
  ProfileName,
  ProfileOutlineBtn,
  ProfilePanel,
  ProfileTab,
  ProfileTabList,
} from "./Profile.styles";

const TABS = [
  { key: "profile", label: "Profile", Icon: UserIcon },
  { key: "billing", label: "Billing", Icon: CardIcon },
  { key: "change_pwd", label: "Change password", Icon: KeyIcon },
  { key: "notifications", label: "Notifications", Icon: BellIcon },
  { key: "favorites", label: "Favorites", Icon: HeartIcon },
];

const ADMIN_ROLE_ID = 1;
const PUBLISHER_ROLE_ID = 3;

/** Members manage a plan. Admin and publisher access is by role. */
const canSeeBilling = (account) => {
  const roleId = Number(account && account.userRoleId);
  return roleId !== ADMIN_ROLE_ID && roleId !== PUBLISHER_ROLE_ID;
};

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function CardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </svg>
  );
}
function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4" />
      <path d="m21 2-9.6 9.6" />
      <circle cx="7.5" cy="15.5" r="5.5" />
    </svg>
  );
}
function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
  );
}
function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}
function ArrowLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m12 19-7-7 7-7" />
      <path d="M19 12H5" />
    </svg>
  );
}

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const initialsFrom = (name) =>
  (name || "")
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

const ProfilePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(readUser);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(
    queryString.parse(location.search).tab || "profile"
  );

  const showBilling = canSeeBilling(user);

  useEffect(() => {
    const staff = !canSeeBilling(user);
    // connectWallet path opens Billing (prod NFT wallet flow)
    if (location.pathname.includes("connectWallet")) {
      if (staff) {
        setActiveTab("profile");
        navigate("/user-profile?tab=profile", { replace: true });
        return;
      }
      setActiveTab("billing");
      return;
    }
    const tab = queryString.parse(location.search).tab || "profile";
    if (staff && tab === "billing") {
      setActiveTab("profile");
      navigate(`${location.pathname}?tab=profile`, { replace: true });
      return;
    }
    setActiveTab(tab);
  }, [location.search, location.pathname, user, navigate]);

  const toggleTab = (tab) => {
    if (activeTab === tab) return;
    setActiveTab(tab);
    // Keep connectWallet path so Billing NFT gate (prod) survives tab switches
    const base = location.pathname.includes("connectWallet")
      ? "/user-profile/connectWallet"
      : location.pathname;
    navigate(`${base}?tab=${tab}`, { replace: true });
  };

  const avatarSrc = user?.avatar?.small;
  const initials = initialsFrom(user?.name);

  return (
    <ProfileMain>
      <ProfileInner>
        <ProfileHero>
          <ProfileHeroRow>
            <ProfileAvatarWrap>
              {avatarSrc ? (
                <ProfileAvatarImg src={avatarSrc} alt={user?.name || ""} />
              ) : (
                <ProfileAvatarFallback>{initials}</ProfileAvatarFallback>
              )}
              <ProfileAvatarEdit
                type="button"
                aria-label="Change profile photo"
                onClick={() => setPhotoOpen(true)}
              >
                <PencilIcon />
              </ProfileAvatarEdit>
            </ProfileAvatarWrap>
            <ProfileHeroText>
              <ProfileName>{user?.name || "Member"}</ProfileName>
              <ProfileEmail>{user?.email || ""}</ProfileEmail>
              <ProfileOutlineBtn type="button" onClick={() => setPhotoOpen(true)}>
                <CameraIcon />
                Change photo
              </ProfileOutlineBtn>
            </ProfileHeroText>
          </ProfileHeroRow>
        </ProfileHero>

        <ProfileTabList role="tablist" aria-label="Profile sections">
          {TABS.filter((tab) => tab.key !== "billing" || showBilling).map(({ key, label, Icon }) => (
            <ProfileTab
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              onClick={() => toggleTab(key)}
            >
              <Icon />
              {label}
            </ProfileTab>
          ))}
        </ProfileTabList>

        <ProfilePanel>
          {activeTab === "profile" && (
            <AccountDetailsPanel onUserUpdated={setUser} />
          )}
          {activeTab === "billing" && showBilling && <BillingPanel />}
          {activeTab === "change_pwd" && <ChangePasswordPanel />}
          {activeTab === "notifications" && <NotificationsPanel />}
          {activeTab === "favorites" && <FavoritesPanel />}
          {activeTab === "analysts" && <AnalystRating />}
        </ProfilePanel>

        <ProfileHomeWrap>
          <ProfileHomeLink to="/">
            <ArrowLeftIcon />
            Back to home
          </ProfileHomeLink>
        </ProfileHomeWrap>
      </ProfileInner>

      <AvatarModal
        open={photoOpen}
        onClose={() => setPhotoOpen(false)}
        userId={user?.id}
      />
    </ProfileMain>
  );
};

export default ProfilePage;
